import React, { useState } from 'react';
import { supabase } from '../../supabaseClient';
import Modal from '../Modal';
import { verifyMetaBeforeSigning, contentHashForMeta } from '../../lib/documentVersion';
import SignatureCapture from './SignatureCapture';
import { dataUrlToBlob } from '../../lib/dataUrl';

// Read-and-sign modal for an acknowledge-type compliance doc. Renders
// the document content (richtext via dangerouslySetInnerHTML, or an
// embedded PDF via <iframe>) and a sign-off form with:
//   - "I have read and agree" checkbox (required)
//   - Signer name input (required, prefilled from supplier_user)
//   - Acknowledge button (disabled until both fields are valid)
//
// On submit inserts a row into document_acknowledgements with:
//   - supplier_id, document_key, document_version (from meta)
//   - signer_name, signer_email (from form + supplier_user)
//   - acknowledged_at = now()
//   - review_decision = null (awaits admin review per the brief's
//                              "approval step required" decision)
//
// After a successful insert, calls onSubmitted() so the parent can
// refetch the compliance hook – the doc moves from OUTSTANDING to
// AWAITING_REVIEW via the synthesised record in useSupplierCompliance.

export default function ComplianceAcknowledgeModal({
  open,
  onClose,
  meta,           // master doc meta from requiredDocs – has key, label, contentRichtext, contentFileUrl, etc.
  supplierUser,   // { id, supplier_id, name, email, … } so we can prefill + stamp
  existingAck,    // optional – when supplier is re-viewing an already-acked doc
  onSubmitted,
}) {
  const [agreed, setAgreed] = useState(false);
  const [signerName, setSignerName] = useState(supplierUser?.name || supplierUser?.full_name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Drawn signature as a PNG data URL, and whether the pad is open. Uploaded
  // to storage on submit – held in memory until then so cancelling the modal
  // leaves nothing behind.
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [capturing, setCapturing] = useState(false);

  if (!open || !meta) return null;

  // Three-mode logic (2026-06 Phase 6):
  //
  //   readOnly  – existingAck matches the current version AND is
  //               either approved or pending review. Supplier just
  //               reviews what they signed; no resign affordance.
  //
  //   reAck     – existingAck exists BUT either (a) was on an older
  //               document_version (admin published a material new
  //               version since), or (b) was rejected on review.
  //               Modal opens in write mode with a "Document
  //               updated" / "Acknowledgement returned" banner so
  //               the supplier sees WHY they're being asked to sign
  //               again. Surfaces meta.changeSummary as the
  //               admin-authored summary of what changed in the
  //               new version.
  //
  //   firstAck  – no existingAck. Standard write mode.
  const versionMismatch = !!(existingAck && meta.versionValue != null
    && existingAck.document_version != null
    && existingAck.document_version !== meta.versionValue);
  const wasRejected = existingAck?.review_decision === 'rejected';
  const reAck = !!existingAck && (versionMismatch || wasRejected);
  const readOnly = !!existingAck && !reAck;

  const handleSubmit = async () => {
    setError(null);
    const clean = String(signerName || '').trim();
    if (!agreed || !clean) {
      setError(!agreed ? 'You need to tick the acknowledgement box.' : 'Type your full name to sign.');
      return;
    }
    if (!signatureDataUrl) {
      setError('Draw your signature to sign this document.');
      return;
    }
    // Evidence check before recording anything (document-versioning §3): the
    // content being acknowledged must still hash to what was published.
    const evidence = verifyMetaBeforeSigning(meta);
    if (evidence.state === 'mismatch') {
      setError('This document has changed since it was published, so it can\'t be acknowledged right now. Please tell Fusion 2K – they\'ll publish it properly as a new version.');
      return;
    }
    setSaving(true);

    // Upload the signature before writing the row. Same bucket and supplier-
    // scoped path as the signed PDFs, so the existing storage policies apply.
    // A failure here STOPS the submission – recording an acknowledgement whose
    // signature didn't save would be worse than not recording it, because the
    // row would look complete.
    const signedAt = new Date().toISOString();
    let signatureUrl = null;
    let signatureBytes = null;
    try {
      // NOT fetch(dataUrl) – the CSP's connect-src blocks data: URLs, which
      // made this fail with "Failed to fetch" for something that never needed
      // the network. See lib/dataUrl.
      const blob = dataUrlToBlob(signatureDataUrl);
      signatureBytes = blob.size;
      const path = `${supplierUser.supplier_id}/signatures/${meta.key}-${signedAt.replace(/[:.]/g, '-')}.png`;
      const { error: upErr } = await supabase.storage
        .from('supplier-uploads')
        .upload(path, blob, { contentType: 'image/png', upsert: true });
      if (upErr) throw new Error(upErr.message);
      const { data: pub } = supabase.storage.from('supplier-uploads').getPublicUrl(path);
      signatureUrl = pub?.publicUrl || null;
      if (!signatureUrl) throw new Error('The signature uploaded but no URL came back.');
    } catch (e) {
      setSaving(false);
      setError(`Couldn't save your signature: ${e.message}. Nothing has been recorded – please try again.`);
      return;
    }

    const payload = {
      supplier_id: supplierUser.supplier_id,
      document_key: meta.key,
      signature_url: signatureUrl,
      signature_captured_at: signedAt,
      signature_bytes: signatureBytes,
      // NOT NULL in the table, so `|| null` was a guaranteed constraint
      // violation on a document with no version_value. 1 matches the
      // COALESCE(version_value, 1) the versioning triggers use.
      document_version: meta.versionValue || 1,
      // Point at the exact version row, and snapshot its content hash – the
      // version number alone only ever meant "whatever row holds that number".
      version_document_id: meta.documentId || null,
      content_hash: evidence.actual || contentHashForMeta(meta),
      signer_name: clean,
      // Also NOT NULL.
      signer_email: supplierUser.email,
      signer_user_id: supplierUser.id || null,
      acknowledged_at: new Date().toISOString(),
      review_decision: null,
    };
    let { data: ackData, error: insertErr } = await supabase
      .from('document_acknowledgements')
      .insert([payload])
      .select('id')
      .single();
    // Deploy-order tolerance: PostgREST rejects the WHOLE insert if a column
    // doesn't exist, so until 2026-08-acknowledgement-signature.sql is run
    // this would block every acknowledgement rather than just the new field.
    // Retry without the signature columns – the signature file is already in
    // storage and the URL is in the audit meta below, so nothing is lost but
    // the column. Once the migration is run this branch never fires.
    if (insertErr && /signature_(url|captured_at|bytes)/.test(insertErr.message || '')) {
      const { signature_url, signature_captured_at, signature_bytes, ...withoutSignature } = payload;
      ({ data: ackData, error: insertErr } = await supabase
        .from('document_acknowledgements')
        .insert([withoutSignature])
        .select('id')
        .single());
    }
    if (insertErr) {
      setSaving(false);
      // 23505 = uq_da_supplier_doc_version_live. After the re-acknowledgement
      // migration this can only mean a LIVE signature already exists for this
      // version — someone else at the company signed while this was open.
      // Before it, it also caught every re-sign after a rejection.
      if (insertErr.code === '23505') {
        setError('This document has already been signed for your company at this version, so a second signature cannot be recorded. Nothing has been saved — refresh to see the signature that was accepted.');
        return;
      }
      // Never show a raw Postgres message to a supplier.
      setError('Could not record your acknowledgement. Nothing has been saved — please try again, and if it keeps happening quote reference ' + (insertErr.code || 'unknown') + '.');
      return;
    }
    // Audit trail (Phase 5) – write a compliance_audit_log row so the
    // SupplierDetail Recent Activity feed surfaces "Acknowledged
    // {doc} as {signer_name}". actor_id intentionally null (a
    // supplier_user signed, not an internal user); supplier_user_id
    // is captured in document_acknowledgements.signer_user_id, and
    // the meta blob carries the doc version + signer name so the
    // activity row reads cleanly without a join.
    await supabase.from('compliance_audit_log').insert([{
      actor_id: null,
      action: 'acknowledged',
      supplier_id: supplierUser.supplier_id,
      document_key: meta.key,
      meta: {
        document_label: meta.label,
        document_version: meta.versionValue || null,
        signer_name: clean,
        signer_user_id: supplierUser.id || null,
        acknowledgement_id: ackData?.id || null,
        // Also here so the signature is recoverable from the audit trail even
        // if the column write fell back above.
        signature_url: signatureUrl,
      },
    }]);
    setSaving(false);
    if (onSubmitted) onSubmitted();
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} maxWidth={760} ariaLabel={`Acknowledge ${meta.label}`}>
      <Modal.Header
        eyebrow={readOnly ? 'Already acknowledged'
          : reAck ? (versionMismatch ? 'Document updated – re-acknowledge required' : 'Acknowledgement returned – sign again')
          : 'Read and acknowledge'}
        title={meta.label}
        sub={readOnly
          ? `You acknowledged this version on ${new Date(existingAck.acknowledged_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}.`
          : reAck && versionMismatch
            ? `Fusion 2K updated this document. Your previous acknowledgement (${new Date(existingAck.acknowledged_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}) was for the older version – please review the current copy and sign again.`
            : reAck && wasRejected
              ? 'Fusion 2K returned your previous acknowledgement. Please review the document and sign again.'
              : 'Read the document below, tick the box, sign with your name, then submit.'}
        onClose={onClose} />
      <Modal.Body>
        {/* Re-ack context banner – surfaces the change_summary the
            admin authored when bumping the version, OR the rejection
            notes when Fusion 2K returned the prior ack. Gives the
            supplier the context they need before re-signing. */}
        {reAck && versionMismatch && meta.changeSummary && (
          <div className="bg-[#E89A2E]/10 border border-[#E89A2E]/40 rounded-lg p-3 mb-4">
            <p className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#7A4E10] mb-1.5">What changed</p>
            <p className="font-['Metropolis'] text-sm text-fusion-mid-grey leading-relaxed whitespace-pre-wrap">{meta.changeSummary}</p>
          </div>
        )}
        {reAck && wasRejected && existingAck?.review_notes && (
          <div className="bg-[#E0290A]/10 border border-[#E0290A]/40 rounded-lg p-3 mb-4">
            <p className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#E0290A] mb-1.5">Fusion 2K's note</p>
            <p className="font-['Metropolis'] text-sm text-fusion-mid-grey leading-relaxed whitespace-pre-wrap">{existingAck.review_notes}</p>
          </div>
        )}
        {/* Document content – file-source docs render the PDF in an
            iframe (no download required to read); richtext-source docs
            render HTML directly. The content area is scrollable so a
            long document doesn't push the sign-off form out of view. */}
        {/* min-w-0 + max-w-full matter as much as the wrapping rules: inside a
            flex column a child sizes to its CONTENT by default, so a long
            unbroken line makes the box wider than the modal and the text ends
            up on one line behind a horizontal scrollbar however it's styled. */}
        <div className="bg-white border border-line rounded-lg p-4 max-h-[420px] overflow-y-auto overflow-x-hidden min-w-0 max-w-full mb-5">
          {meta.source === 'file' && meta.contentFileUrl ? (
            <iframe
              src={meta.contentFileUrl}
              title={meta.label}
              className="w-full h-[380px] bg-white rounded"
            />
          ) : meta.contentRichtext ? (
            <div
              className="font-['Metropolis'] text-sm text-fusion-mid-grey leading-relaxed prose-compliance"
              // Content authored by Fusion admins in the doc config
              // modal. Rendered as HTML so paragraph breaks, lists, and
              // emphasis come through.
              dangerouslySetInnerHTML={{ __html: meta.contentRichtext }}
            />
          ) : (
            <p className="font-['Metropolis'] text-sm text-[#51534A] italic">
              This document has no content configured. Contact Fusion to resolve.
            </p>
          )}
        </div>

        {/* Sign-off form – disabled when already acknowledged. */}
        <div className={`bg-white border border-line rounded-lg p-4 ${readOnly ? 'opacity-60' : ''}`}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={readOnly ? true : agreed}
              disabled={readOnly || saving}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-active-green cursor-pointer flex-shrink-0"
            />
            <span className="font-['Metropolis'] text-[13px] text-ink leading-snug">
              I have read and agree to the terms of <span className="font-bold">{meta.label}</span> on behalf of my organisation.
            </span>
          </label>
          <div className="mt-4">
            <label className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#51534A] mb-1 block">
              Signed by
            </label>
            <input
              type="text"
              value={readOnly ? (existingAck?.signer_name || '') : signerName}
              onChange={(e) => setSignerName(e.target.value)}
              disabled={readOnly || saving}
              placeholder="Type your full name"
              className="w-full bg-white border border-line rounded-md px-3 py-2 text-sm text-ink outline-none font-['Metropolis'] focus:border-[#92D400] disabled:opacity-50"
            />
            <p className="font-['Metropolis'] text-[10px] text-[#51534A] mt-1.5 tracking-wide">
              Your printed name, shown beneath your signature. Date stamp is captured automatically.
            </p>
          </div>

          {/* Drawn signature. A typed name and a tick is an attestation; the
              pdf_sign flow has always had suppliers actually sign, and an
              acknowledge document is no less binding for having no PDF behind
              it. Rows signed before this existed have no image and say so,
              rather than being dressed up as something they weren't. */}
          <div className="mt-4">
            <label className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#51534A] mb-1 block">
              Signature
            </label>
            {readOnly ? (
              existingAck?.signature_url ? (
                <img
                  src={existingAck.signature_url}
                  alt={`Signature of ${existingAck.signer_name || 'the signer'}`}
                  className="bg-white border border-line rounded-md max-h-[110px] w-auto" />
              ) : (
                <p className="font-['Metropolis'] text-[12px] text-[#51534A] italic">
                  Typed signature – this was signed before drawn signatures were introduced.
                </p>
              )
            ) : signatureDataUrl ? (
              <div className="flex items-end gap-3 flex-wrap">
                <img
                  src={signatureDataUrl}
                  alt="Your signature"
                  className="bg-white border border-line rounded-md max-h-[110px] w-auto" />
                <button
                  type="button"
                  onClick={() => setCapturing(true)}
                  disabled={saving}
                  className="font-['Metropolis'] text-[11px] font-semibold text-[#2E8FA1] hover:underline disabled:opacity-50">
                  Sign again
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setCapturing(true)}
                disabled={saving}
                className="w-full bg-white border border-dashed border-[#BFC7AE] rounded-md px-3 py-4 font-['Metropolis'] text-[12px] font-semibold text-[#2E8FA1] hover:border-[#2E8FA1] hover:bg-[#2E8FA1]/[0.04] transition-colors disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2E8FA1]">
                Draw your signature
              </button>
            )}
          </div>
          {error && (
            <p className="font-['Metropolis'] text-[12px] text-[#E0290A] mt-3 font-semibold">{error}</p>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="bg-white border border-line rounded font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase text-ink px-4 py-2 hover:border-[#DADCCD] transition-colors">
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving || !agreed || !String(signerName || '').trim() || !signatureDataUrl}
              className="bg-[#92D400] border border-[#92D400] rounded font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase text-[#131313] px-4 py-2 hover:bg-[#a4e810] transition-colors disabled:opacity-50">
              {saving ? 'Recording…' : 'Acknowledge'}
            </button>
          )}
        </div>
      </Modal.Footer>
      {/* Draw-to-sign pad, shared with the PDF signer so a signature is
          captured the same way wherever it's given. */}
      {capturing && (
        <SignatureCapture
          onDone={(dataUrl) => { setSignatureDataUrl(dataUrl); setCapturing(false); setError(null); }}
          onCancel={() => setCapturing(false)} />
      )}
    </Modal>
  );
}
